"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";

interface ProductCategoryProps {
  data: {
    id: number;
    title?: string;
    description?: string;
    isLive: boolean;
    useH1Tag: boolean;
    Card: {
      id: number;
      title?: string;
      description?: string;
      href?: string;
      screenDisplay: boolean;
      badge?: string;
      image?: {
        data?: {
          attributes?: {
            url: string;
            alternativeText: string | null;
          };
        };
      };
    }[];
  };
}

const TopPicksEditorial = ({ data }: ProductCategoryProps) => {
  if (!data?.isLive) return null;

  const titleParts = data?.title?.split(" ") || [];
  const firstPart = titleParts.slice(0, 2).join(" ") || "Our Top";
  const restPart = titleParts.slice(2).join(" ") || "Picks";

  const cards = data?.Card?.filter((c) => c?.screenDisplay) || [];

  return (
    <section className="py-24 bg-white px-6 md:px-12 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center mb-16 space-y-4 text-center">
          <div className="flex items-center gap-4">
            <span className="h-[1.5px] w-12 bg-[var(--secondary)]"></span>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-700">
              The Selection
            </p>
            <span className="h-[1.5px] w-12 bg-[var(--secondary)]"></span>
          </div>

          <div className="text-4xl md:text-6xl font-black text-neutral-900 uppercase italic tracking-tighter leading-none">
            {data.useH1Tag ? (
              <h1>
                {firstPart}{" "}
                <span className="text-[var(--secondary)]">
                  {restPart}
                </span>
              </h1>
            ) : (
              <h2>
                {firstPart}{" "}
                <span className="text-[var(--secondary)]">
                  {restPart}
                </span>
              </h2>
            )}
          </div>

          <p className="text-[11px] font-bold text-gray-700 uppercase tracking-[0.2em]">
            {data.description ||
              "Curated by style experts for the next generation"}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
          {cards.map((item, index) => {
            const imageUrl = item?.image?.data?.attributes?.url;

            return (
              <div
                key={item?.id || index}
                className="group relative flex flex-col cursor-pointer"
              >
                <Link href={item?.href || "#"}>
                  <div className="relative aspect-[3/4] overflow-hidden bg-neutral-100 mb-6">
                    {imageUrl ? (
                      <Image
                        src={imageUrl}
                        alt={item?.title || "Category"}
                        fill
                        quality={80}
                        sizes="(max-width: 768px) 50vw, 25vw"
                        className="object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700 ease-out"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-neutral-200" />
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />

                    {item.badge && (
                      <div className="absolute top-0 left-0 bg-[var(--secondary)] text-white px-4 py-2 text-[10px] font-black uppercase tracking-widest italic shadow-lg z-20">
                        {item.badge}
                      </div>
                    )}

                    <div className="absolute bottom-6 right-6 bg-white text-neutral-900 p-2.5 transform translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300 z-20">
                      <ArrowUpRight size={20} />
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--secondary)] translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                      {item.description || "Discover more"}
                    </p>

                    <h3 className="text-xl md:text-2xl font-black text-neutral-900 uppercase italic tracking-tighter leading-none group-hover:text-[var(--secondary)] transition-colors">
                      {item.title}
                    </h3>

                    <div className="pt-2">
                      <div className="w-0 h-[2px] bg-[var(--secondary)] group-hover:w-full transition-all duration-500" />
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Footer Link */}
        <div className="mt-20 flex justify-center">
          <Link
            href="/collections"
            className="group flex items-center gap-4 text-[11px] font-black uppercase tracking-[0.3em] text-neutral-900 border-b-2 border-neutral-900 pb-2 hover:text-[var(--secondary)] hover:border-[var(--secondary)] transition-colors"
          >
            View Full Series
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-[2px]" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TopPicksEditorial;
